import * as React from 'react';

import {useQuery} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {SalesDocumentSortOrder} from 'nutshell-graphql-types';
import type {
    GetFilteredQuotes as GetFilteredQuotesQuery,
    GetFilteredQuotesVariables as GetFilteredQuotesVariables,
    QuotesSearchInput,
    FilteredQuoteFragment,
} from 'nutshell-graphql-types';

import {getSortOrderEnum} from '../../../shared/helper';

import {nutApolloClient} from '../../../../../../apollo-client';

import GET_FILTERED_QUOTES from '../queries/get-filtered-quotes.graphql';

export const DEFAULT_PAGE_SIZE = 50;

export const useGetFilteredQuotes = (
    input: QuotesSearchInput
): {
    quotes: FilteredQuoteFragment[];
    numQuotes: number;
    isLoading: boolean;
    error: string | null | undefined;
    refetch: () => Promise<any>;
    fetchNextPage: () => Promise<any>;
    fetchPreviousPage: () => Promise<any>;
    fetchFirstPageWithNewSort: (sort: string) => Promise<any>;
} => {
    const sort = getSort(input.sort);
    const [currentSort, setCurrentSort] = React.useState(sort);

    const originalQueryVariables = {
        input: {
            ...input,
            first: DEFAULT_PAGE_SIZE,
            sort: currentSort,
        },
    } as const;

    const {loading, data, error, refetch, fetchMore} = useQuery<
        GetFilteredQuotesQuery,
        GetFilteredQuotesVariables
    >(GET_FILTERED_QUOTES, {
        variables: originalQueryVariables,
        fetchPolicy: 'cache-and-network',
    });

    const quotes = data ? data.quotes : null;

    const allQuotes = quotes ? quotes.edges.map((edge) => edge.node) : [];

    const pageInfo = quotes ? quotes.pageInfo : null;

    const handleFetchPreviousPage = () => {
        if (pageInfo && pageInfo.hasPreviousPage) {
            const nextRequestVariables = {
                input: {
                    ...input,
                    sort: currentSort,
                    before: pageInfo.startCursor,
                    last: DEFAULT_PAGE_SIZE,
                },
            } as const;

            return fetchMore({
                variables: nextRequestVariables,
                // @ts-expect-error
                updateQuery: (
                    prev: GetFilteredQuotesQuery,
                    newResults: {
                        fetchMoreResult: any;
                    }
                ) => {
                    // fetchMore will not cache this data under nextRequestVariables, so we need to do this manually
                    // so future calls with the same variables can rely on the cache.
                    nutApolloClient.writeQuery({
                        query: GET_FILTERED_QUOTES,
                        variables: nextRequestVariables,
                        data: newResults.fetchMoreResult,
                    });

                    return newResults.fetchMoreResult;
                },
            });
        }

        return Promise.reject();
    };

    const handleFetchNextPage = () => {
        if (pageInfo && pageInfo.hasNextPage) {
            const nextRequestVariables = {
                input: {
                    ...input,
                    sort: currentSort,
                    after: pageInfo.endCursor,
                    first: DEFAULT_PAGE_SIZE,
                },
            } as const;

            return fetchMore({
                variables: nextRequestVariables,
                // @ts-expect-error
                updateQuery: (
                    prev: GetFilteredQuotesQuery,
                    newResults: {
                        fetchMoreResult: GetFilteredQuotesQuery;
                    }
                ) => {
                    nutApolloClient.writeQuery({
                        query: GET_FILTERED_QUOTES,
                        variables: nextRequestVariables,
                        data: newResults.fetchMoreResult,
                    });

                    return newResults.fetchMoreResult;
                },
            });
        }

        return Promise.reject();
    };

    const handleFetchFirstPageWithNewSort = (newSort: string) => {
        const newSortEnum = getSort(newSort);
        setCurrentSort(newSortEnum);

        const newRequestVariables = {
            input: {
                ...input,
                first: DEFAULT_PAGE_SIZE,
                sort: newSortEnum,
            },
        } as const;

        return fetchMore({
            variables: newRequestVariables,
            // @ts-expect-error
            updateQuery: (
                prev: GetFilteredQuotesQuery,
                newResults: {
                    fetchMoreResult: GetFilteredQuotesQuery;
                }
            ) => {
                nutApolloClient.writeQuery({
                    query: GET_FILTERED_QUOTES,
                    variables: newRequestVariables,
                    data: newResults.fetchMoreResult,
                });

                return newResults.fetchMoreResult;
            },
        });
    };

    return {
        quotes: allQuotes,
        numQuotes: quotes ? quotes.totalCount : 0,
        isLoading: loading,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
        refetch,
        fetchNextPage: handleFetchNextPage,
        fetchPreviousPage: handleFetchPreviousPage,
        fetchFirstPageWithNewSort: handleFetchFirstPageWithNewSort,
    };
};

function getSort(sort?: string | null): SalesDocumentSortOrder | null | undefined {
    if (!sort) {
        return null;
    }

    return getSortOrderEnum(sort);
}
